import { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { pick } from "lodash-es";
import { useEffect, useMemo, useState, useRef } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";

import { PropertyValue, SubSchema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/datasets/DatasetTable/FloatingWindow";
import { Callout } from "src/design-system/Callout";
import {
  createDefaultAjv,
  isInternalSchemaDefKey,
  noAllOf,
  noInternalSchemaDefs,
  noNullOnlyTypes,
  validateJsonSchema,
} from "src/jsonSchema/validateJsonSchema";
import { SchemaOptions } from "src/router/SearchParams";
import { SchemaConverter, SchemaUI } from "src/schema/schemaMappingUtils";
import { useCodeMirrorEventListener } from "src/schema/window/useCodeMirrorEventListener";
import { useFlowJsonSchemaWindow } from "src/schema/window/useFlowJsonSchemaWindow";
import {
  applyHighlightAndScroll,
  stringifySchema,
} from "src/schema/window/utils";

const Title = () => {
  return (
    <div className="flex flex-col justify-self-start">
      <h3 className="text-gray-800 font-inter-semibold-13px">
        Generate from JSON Schema
      </h3>
      <p className="text-gray-500 font-inter-normal-12px">
        Paste a JSON schema below to generate your input schema
      </p>
    </div>
  );
};

type SubscribeCallback = (value: SchemaUI) => void;

export type FlowJsonSchemaWindowData = {
  intialValue: SchemaUI;
  schemaType: SchemaOptions;
  highlightPath?: string;
  onEdit: (isDirty: boolean) => void;
  onChange: (value: SchemaUI) => void;
  onClose: () => void;
  subscribe: (callback: SubscribeCallback) => VoidFunction;
};

type FlowJsonSchemaWindowForm = {
  value: string;
};

const toBackendSchemaString = (
  schemaUI: SchemaUI,
  schemaType: SchemaOptions,
) => {
  const backendSchema = SchemaConverter.uiToBE(schemaUI, schemaType);
  return stringifySchema(backendSchema);
};

const validator =
  (
    internalSchemaDefs: Record<string, SubSchema<PropertyValue>>,
  ): Resolver<FlowJsonSchemaWindowForm> =>
  ({ value }) => {
    const trimmedValue = value?.trim() ?? "";
    if (!trimmedValue) {
      return {
        values: {} as Record<string, never>,
        errors: {
          value: { message: "Value is required", type: "value" },
        },
      };
    }

    const error = validateJsonSchema(
      trimmedValue,
      [noAllOf, noInternalSchemaDefs, noNullOnlyTypes],
      createDefaultAjv(true),
      internalSchemaDefs,
    );
    if (error) {
      return {
        values: {} as Record<string, never>,
        errors: { value: { message: error.message, type: "value" } },
      };
    }

    return {
      errors: {} as Record<string, never>,
      values: { value },
    };
  };

const extractInternalSchemaDefs = (
  schema: SchemaUI,
  schemaType: SchemaOptions,
) => {
  const backendSchema = SchemaConverter.uiToBE(schema, schemaType);
  const keysToExtract = Object.keys(backendSchema.$defs ?? {}).filter((key) =>
    isInternalSchemaDefKey(key),
  );

  return pick(backendSchema.$defs ?? {}, keysToExtract);
};

export const FlowJsonSchemaWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  data,
}) => {
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const { closeWindow } = useFlowJsonSchemaWindow();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const {
    intialValue,
    highlightPath,
    schemaType,
    onChange,
    onEdit,
    onClose,
    subscribe,
  } = data as FlowJsonSchemaWindowData;

  const internalSchemaDefs = useMemo(
    () => extractInternalSchemaDefs(intialValue, schemaType),
    [intialValue, schemaType],
  );

  const {
    control,
    formState: { isDirty },
    reset,
    getFieldState,
    getValues,
    handleSubmit,
  } = useForm<FlowJsonSchemaWindowForm>({
    defaultValues: { value: toBackendSchemaString(intialValue, schemaType) },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: validator(internalSchemaDefs),
  });

  const [openConfirmationModal, setOpenConfirmationModal] = useState(isDirty);

  const handleCloseAllModals = () => {
    setOpenConfirmationModal(false);
    closeWindow();
    onClose();
  };

  const handleOnClose = () => {
    if (isDirty) {
      return setOpenConfirmationModal(true);
    }
    handleCloseAllModals();
  };

  useEffect(() => {
    return subscribe((values) => {
      if (!getFieldState("value").isDirty) {
        reset({ value: toBackendSchemaString(values, schemaType) });
      }
    });
  }, [subscribe, getFieldState, reset, schemaType]);

  useCodeMirrorEventListener(() => {
    if (highlightPath && editorRef.current?.view) {
      applyHighlightAndScroll(editorRef.current.view, highlightPath);
    }
  });

  const onSubmit = () => {
    const beSchemaStr = getValues("value");
    const beSchema = JSON.parse(beSchemaStr);
    onChange(
      SchemaConverter.beToUI(
        {
          ...beSchema,
          $defs: {
            ...internalSchemaDefs,
            ...beSchema.$defs,
          },
        },
        schemaType,
      ),
    );
    reset({}, { keepValues: true, keepDirty: false });
    onEdit(false);
  };

  return (
    <FloatingWindowInternal
      ref={windowRef}
      height="h-178"
      isPinned={false}
      minHeight="h-160"
      minWidth="w-140"
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={<Title />}
      width="w-161"
      draggable
      lockSchemaEditor
      maximizable
      resizeable
      onClose={handleOnClose}
    >
      <div className="flex h-full w-full flex-col gap-y-3 overflow-hidden p-2 pt-4">
        <Controller
          control={control}
          name="value"
          render={({ field, fieldState }) => (
            <>
              {fieldState.error?.message && (
                <Callout type="error">{fieldState.error?.message}</Callout>
              )}
              <div className="flex-grow overflow-hidden">
                <CodeEditor
                  ref={editorRef}
                  backgroundColor="white"
                  basicSetup={{
                    lineNumbers: true,
                    highlightActiveLine: true,
                  }}
                  dataLoc="schema-json-view-input"
                  drawErrorBorder={false}
                  fontFamily="inter"
                  language="jsonschema"
                  value={field.value}
                  autoFocus
                  highlightOnFocus
                  lineWrapping
                  onBlur={field.onBlur}
                  onChange={(value) => {
                    field.onChange(value);
                    onEdit(fieldState.isDirty);
                  }}
                />
              </div>
            </>
          )}
        />
        <div className="flex justify-end">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Button
              dataLoc="schema-json-view-save"
              disabled={!isDirty}
              htmlType="submit"
              variant="primary"
              fullWidth
            >
              Generate
            </Button>
          </form>
        </div>
      </div>
      <ConfirmationModal
        description="Changes made in the JSON view haven't been saved yet. Closing this window will discard them."
        open={openConfirmationModal}
        title="Unsaved schema edits"
        onClose={() => setOpenConfirmationModal(false)}
        onConfirm={handleCloseAllModals}
      />
    </FloatingWindowInternal>
  );
};
